import React, { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';

interface GalleryImage {
  src: string;
  thumbnail: string;
  alt: string;
  title: string;
  description: string;
}

const galleryImages: GalleryImage[] = [
  {
    src: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Free_Pomodoro_Timer_Alipne_View.png",
    thumbnail: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Thumbnails/Free_Pomodoro_Timer_Alipne_View_Thumbnail.jpg",
    alt: "Majestic alpine mountain peaks with snow-capped summits creating perfect backdrop for Pomodoro focus sessions and deep work concentration",
    title: "Alpine View",
    description: "Mountain landscape for focused work sessions"
  },
  {
    src: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Free_Pomodoro_Timer_Alpine_Walking_Trail.png", 
    thumbnail: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Thumbnails/Free_Pomodoro_Timer_Alpine_Walking_Trail_Thumb.jpg",
    alt: "Serene alpine walking trail through peaceful mountain scenery ideal for Pomodoro technique break intervals and nature reflection",
    title: "Alpine Walking Trail",
    description: "Nature trail for productive breaks"
  },
  {
    src: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Free_Pomodoro_Timer_Aussie_Coast.png",
    thumbnail: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Thumbnails/Free_Pomodoro_Timer_Aussie_Coast_Thumb.jpg",
    alt: "Stunning Australian coastal landscape with crystal clear waters promoting calm concentration during Pomodoro work cycles",
    title: "Aussie Coast",
    description: "Coastal scenery for calm concentration"
  },
  {
    src: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Free_Pomodoro_Timer_Forest_View.png",
    thumbnail: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Thumbnails/Free_Pomodoro_Timer_Forest_View_Thumb.jpg",
    alt: "Lush forest canopy view with natural greenery enhancing focus and mental clarity for effective Pomodoro technique practice",
    title: "Forest View",
    description: "Woodland scenes for natural focus"
  },
  {
    src: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Free_Pomodoro_Timer_Golden_Hour.png",
    thumbnail: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Thumbnails/Free_Pomodoro_Timer_Golden_Hour_Thumb.jpg",
    alt: "Golden hour sunset providing warm inspiring light perfect for peak productivity during evening Pomodoro work sessions",
    title: "Golden Hour", 
    description: "Sunset timing for peak productivity"
  },
  {
    src: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Free_Pomodoro_Timer_Monument_Valley.png",
    thumbnail: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Thumbnails/Free_Pomodoro_Timer_Monument_Valley_Thumb.jpg",
    alt: "Iconic Monument Valley desert landscape showcasing minimalist clarity essential for deep work and Pomodoro concentration",
    title: "Monument Valley",
    description: "Desert clarity for deep work"
  },
  {
    src: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Free_Pomodoro_Timer_New_York.png",
    thumbnail: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Thumbnails/Free_Pomodoro_Timer_New_York_Thumb.jpg",
    alt: "Dynamic New York City urban skyline energizing productive work sessions with metropolitan focus during Pomodoro cycles",
    title: "New York",
    description: "Urban energy for dynamic productivity"
  },
  {
    src: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Free_Pomodoro_Timer_Richmond_Morning.png",
    thumbnail: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Thumbnails/Free_Pomodoro_Timer_Richmond_Morning_Thumb.jpg",
    alt: "Peaceful Richmond morning dawn scene inspiring fresh starts and renewed focus for beginning Pomodoro work sessions",
    title: "Richmond Morning",
    description: "Dawn inspiration for fresh starts"
  },
  {
    src: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Free_Pomodoro_Timer_Sunset.png",
    thumbnail: "https://sbgdaxkzktxlojssxplo.supabase.co/storage/v1/object/public/site-content/Thumbnails/Free_Pomodoro_Timer_Sunset_Thumb.jpg",
    alt: "Tranquil sunset horizon perfect for evening reflection and completing productive Pomodoro technique work cycles",
    title: "Sunset",
    description: "Evening reflection for work completion"
  }
];

const PomodoroGallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<number>>(new Set());

  const openModal = (index: number) => setSelectedImage(index);
  const closeModal = () => setSelectedImage(null);

  const nextImage = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % galleryImages.length);
    }
  };

  const prevImage = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === 0 ? galleryImages.length - 1 : selectedImage - 1);
    }
  };

  const handleImageError = (index: number) => {
    setImageLoadErrors(prev => new Set([...prev, index]));
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (selectedImage === null) return;
      
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          prevImage();
          break;
        case 'ArrowRight':
          event.preventDefault();
          nextImage();
          break;
        case 'Escape':
          event.preventDefault();
          closeModal();
          break;
      }
    };

    if (selectedImage !== null) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage]);

  const isMobile = useIsMobile();

  const renderImageItem = (image: GalleryImage, index: number) => (
    <div
      key={index}
      className="group relative cursor-pointer overflow-hidden rounded-lg border border-border hover:border-primary transition-all duration-300 bg-muted break-inside-avoid mb-4"
      onClick={() => openModal(index)}
    >
      {imageLoadErrors.has(index) && (
        <div className="absolute inset-0 bg-muted flex items-center justify-center z-10">
          <div className="text-center p-4">
            <div className="w-12 h-12 mx-auto mb-2 text-muted-foreground">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-xs text-muted-foreground">Image unavailable</p>
          </div>
        </div>
      )}
      
      <img
        src={image.thumbnail}
        alt={image.alt}
        className={`w-full object-cover object-center transition-transform duration-300 group-hover:scale-[1.02] bg-muted ${
          isMobile ? 'h-48' : 'h-auto min-h-[120px]'
        }`}
        onError={() => handleImageError(index)}
        loading="lazy"
        decoding="async"
      />
      
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <h4 className="text-white text-sm font-medium">{image.title}</h4>
        <p className="text-white/80 text-xs">{image.description}</p>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {isMobile ? (
        <Carousel
          opts={{ align: "start", loop: true }}
          className="w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {galleryImages.map((image, index) => (
              <CarouselItem key={index} className="pl-2 md:pl-4 basis-4/5">
                {renderImageItem(image, index)}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      ) : (
        <div className="columns-2 md:columns-3 gap-4 space-y-4">
          {galleryImages.map((image, index) => renderImageItem(image, index))}
        </div>
      )}

      {selectedImage !== null && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/90">
          <div className="flex justify-between items-center p-4 bg-black/20 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              <button
                onClick={prevImage}
                className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                aria-label="Previous image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={nextImage}
                className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                aria-label="Next image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <span className="text-white text-sm">
                {selectedImage + 1} / {galleryImages.length}
              </span>
            </div>

            <button
              onClick={closeModal}
              className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              aria-label="Close gallery"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
            <img
              src={galleryImages[selectedImage].src}
              alt={galleryImages[selectedImage].alt}
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
              loading="eager"
              decoding="async"
            />
          </div>

          <div className="p-4 bg-black/20 backdrop-blur-sm">
            <h3 className="text-lg font-semibold mb-1 text-white">{galleryImages[selectedImage].title}</h3>
            <p className="text-sm text-white/70">{galleryImages[selectedImage].description}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PomodoroGallery;